import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Microphone, Stop, X, Plus, ArrowsClockwise } from "@phosphor-icons/react";

// Pick the best MediaRecorder mimeType the browser supports.
function pickMime() {
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  for (const m of cands) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (e) { /* ignore */ }
  }
  return "";
}

const extFor = (mime) => {
  if (!mime) return "webm";
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("wav")) return "wav";
  return "webm";
};

/**
 * VoiceRecorder — press-to-record, releases into a transcript preview with
 * Append/Replace/Discard actions. Transcribes via /api/voice/transcribe (Whisper).
 *
 * Props:
 *  - value: current textarea value (used to append cleanly)
 *  - onChange: (newText) => void  (called for Append or Replace)
 *  - prompt: optional context string to prime Whisper for domain accuracy
 *  - language: ISO code (default "en")
 *  - disabled: bool
 *  - testid: base testid; buttons get -start/-stop/-append/-replace/-discard suffixes
 */
export default function VoiceRecorder({ value = "", onChange, prompt = "", language = "en", disabled = false, testid = "voice" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing | preview
  const [seconds, setSeconds] = useState(0);
  const [text, setText] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => hardCleanup(), []);

  const hardCleanup = () => {
    try {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    } catch (e) { /* ignore */ }
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (e) { /* ignore */ }
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
    timerRef.current = null;
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Voice input isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = handleStop;
      rec.start();
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied. Grant mic permission and try again.");
      hardCleanup();
    }
  };

  const stop = () => {
    try {
      if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    } catch (e) { /* ignore */ }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const handleStop = async () => {
    const mime = recRef.current?.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    // release mic
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (e) { /* ignore */ }
    chunksRef.current = [];

    if (!blob || blob.size < 800) {
      toast.error("Recording too short — try again.");
      setState("idle");
      return;
    }
    setState("transcribing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, `recording.${extFor(mime)}`);
      fd.append("language", language || "en");
      if (prompt) fd.append("prompt", prompt);
      const { data } = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const t = (data?.text || "").trim();
      if (!t) {
        toast.error("No speech detected. Try again.");
        setState("idle");
        return;
      }
      setText(t);
      setState("preview");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Transcription failed"));
      setState("idle");
    }
  };

  const doAppend = () => {
    const existing = value || "";
    const sep = existing.trim() ? (existing.endsWith("\n") ? "" : "\n\n") : "";
    onChange?.(existing + sep + text);
    toast.success("Appended");
    setText(""); setState("idle");
  };
  const doReplace = () => {
    onChange?.(text);
    toast.success("Replaced");
    setText(""); setState("idle");
  };
  const doDiscard = () => { setText(""); setState("idle"); };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="inline-flex items-start gap-2" data-testid={testid}>
      {state === "idle" && (
        <button type="button" onClick={start} disabled={disabled} data-testid={`${testid}-start`}
                title="Dictate this field"
                className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-900 hover:text-white transition-colors duration-200 disabled:opacity-50">
          <Microphone size={12} weight="fill"/> Voice
        </button>
      )}
      {state === "recording" && (
        <div className="inline-flex items-center gap-2 border border-red-300 bg-red-50 rounded-sm px-2 py-1 text-xs" data-testid={`${testid}-recording`}>
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"/>
          <span className="tabular-nums font-mono text-red-700">{mmss(seconds)}</span>
          <button type="button" onClick={stop} data-testid={`${testid}-stop`} className="ml-1 inline-flex items-center gap-1 text-red-700 hover:text-red-900 font-semibold">
            <Stop size={12} weight="fill"/> Stop
          </button>
        </div>
      )}
      {state === "transcribing" && (
        <div className="text-xs text-slate-600 inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1" data-testid={`${testid}-transcribing`}>
          <ArrowsClockwise size={12} className="animate-spin"/> Transcribing…
        </div>
      )}
      {state === "preview" && (
        <div className="w-full min-w-[260px] border border-blue-300 bg-blue-50 rounded-sm p-3 text-xs" data-testid={`${testid}-preview`}>
          <div className="label-micro mb-1">Transcript</div>
          <div className="text-slate-800 whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto" data-testid={`${testid}-transcript-text`}>{text}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={doAppend} data-testid={`${testid}-append`}
                    className="text-xs inline-flex items-center gap-1 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-2.5 py-1 transition-colors duration-200">
              <Plus size={12}/> Append
            </button>
            <button type="button" onClick={doReplace} data-testid={`${testid}-replace`}
                    className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1 hover:bg-slate-100">
              <ArrowsClockwise size={12}/> Replace
            </button>
            <button type="button" onClick={doDiscard} data-testid={`${testid}-discard`}
                    className="text-xs inline-flex items-center gap-1 text-red-600 hover:underline ml-auto">
              <X size={12}/> Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
